import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Youtube, Heart, Phone, MapPin, Star, Cross, Shield, Crown } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Cuvânt și Rugăciune",
    "description": "Intră în legătură cu echipa Cuvânt și Rugăciune și fii parte din comunitatea noastră de credință",
    "url": "https://cuvantsirugaciune.ro/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Cuvânt și Rugăciune",
      "founder": {
        "@type": "Person",
        "name": "Mircea Stanciu"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+40",
          "contactType": "customer service",
          "email": "virgilstaciu31@gmail.com"
        }
      ],
      "sameAs": [
        "https://www.youtube.com/@mircea_stanciu",
        "https://chat.whatsapp.com/DqlrnJxLjzwHKtsSAgGbaJ"
      ]
    }
  };

  const contactMethods = [
    {
      icon: Youtube,
      title: "YouTube",
      description: "Urmărește-ne pe YouTube pentru episoadele video și live streams",
      action: "Vizitează Canalul",
      link: "https://www.youtube.com/@mircea_stanciu",
      color: "text-red-400"
    },
    {
      icon: Mail,
      title: "Email",
      description: "Trimite-ne un email pentru întrebări sau sugestii",
      action: "Scrie-ne",
      link: "mailto:virgilstaciu31@gmail.com",
      color: "text-blue-400"
    },
    {
      icon: Heart,
      title: "Comunitate WhatsApp",
      description: "Alătură-te comunității noastre pentru discuții și rugăciune",
      action: "Alătură-te",
      link: "https://chat.whatsapp.com/DqlrnJxLjzwHKtsSAgGbaJ",
      color: "text-green-400"
    }
  ];

  return (
    <div className="bg-slate-900 text-white overflow-hidden">
      <SEOHead 
        title="Contact Cuvânt și Rugăciune - Mircea Stanciu | Podcast Creștin"
        description="Intră în legătură cu echipa Cuvânt și Rugăciune și fii parte din comunitatea noastră de credință. Contact prin email, YouTube și WhatsApp pentru întrebări, sugestii și comunitate."
        keywords="contact mircea stanciu, contact podcast crestin, comunitate crestina romania, youtube podcast, email contact, whatsapp comunitate crestina"
        canonicalUrl="/contact"
        ogTitle="Contact - Comunitatea Cuvânt și Rugăciune cu Mircea Stanciu"
        ogDescription="Conectează-te cu comunitatea noastră creștină. Găsește modalități de contact și alătură-te conversațiilor despre credință."
        structuredData={structuredData}
      />
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.8)), url('https://images.unsplash.com/photo-1617610882105-71c5d7447837')`
          }}
        />
        
        {/* Decorative shield pattern */}
        <div className="absolute top-6 left-6 text-amber-400/10">
          <Shield className="w-16 h-16" />
        </div>
        <div className="absolute bottom-6 right-6 text-amber-400/10">
          <Crown className="w-20 h-20" />
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
              <Cross className="w-10 h-10 sm:w-16 sm:h-16 text-amber-400" />
              <Mail className="w-8 h-8 sm:w-12 sm:h-12 text-amber-400" />
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent leading-tight">
              Contact și Comunitate
            </h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-3 sm:mb-4 leading-relaxed px-4"
            >
              Intră în legătură cu noi și fii parte din comunitatea noastră de credință
            </motion.p>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-base sm:text-lg text-amber-400 mb-6 sm:mb-8 font-medium"
            >
              "Căci unde sunt doi sau trei adunați în Numele Meu, acolo sunt și Eu în mijlocul lor" - Matei 18:20
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-16 sm:py-20 bg-slate-800/50 relative">
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">Modalități de Contact</h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Alege modalitatea care ți se potrivește cel mai bine pentru a intra în legătură cu noi
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => window.open(method.link, '_blank')}
                className="text-center p-6 sm:p-8 bg-slate-700/50 rounded-3xl border border-amber-400/20 hover:border-amber-400/40 transition-all duration-300 cursor-pointer group hover:transform hover:scale-105"
              >
                <div className="flex justify-center items-center space-x-2 mb-4">
                  <method.icon className={`w-8 h-8 sm:w-12 sm:h-12 ${method.color} group-hover:scale-110 transition-transform`} />
                  <Star className="w-4 h-4 text-amber-300" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-amber-400 mb-3">{method.title}</h3>
                <p className="text-sm sm:text-base text-slate-300 mb-4 leading-relaxed">
                  {method.description}
                </p>
                <span className="text-amber-400 hover:text-amber-300 transition-colors font-semibold text-sm sm:text-base">
                  {method.action} →
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Information Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-8 h-8 text-amber-400" />
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400">Comunitatea Noastră</h2>
              </div>
              <p className="text-base sm:text-lg text-slate-300 mb-4 sm:mb-6 leading-relaxed">
                Suntem o comunitate de credincioși uniți prin dragostea pentru Cuvântul lui Dumnezeu 
                și dorința de a crește spiritual împreună.
              </p>
              <p className="text-base sm:text-lg text-slate-300 mb-6 leading-relaxed">
                Prin intermediul canalului nostru YouTube și grupurilor WhatsApp, 
                împărtășim învățături biblice, rugăciuni și sprijin mutual în credință.
              </p>
              
              <div className="space-y-4">
                <div className="bg-slate-700/50 p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Youtube className="w-5 h-5 text-red-400" />
                    <h4 className="font-semibold text-amber-400">Canal YouTube</h4>
                  </div>
                  <p className="text-sm text-slate-300">Episoade noi cu studii biblice și meditații spirituale</p>
                </div>
                
                <div className="bg-slate-700/50 p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Heart className="w-5 h-5 text-green-400" />
                    <h4 className="font-semibold text-amber-400">Grupuri WhatsApp</h4>
                  </div>
                  <p className="text-sm text-slate-300">Discuții zilnice, rugăciuni comune și sprijin frățesc</p>
                </div>
                
                <div className="bg-slate-700/50 p-4 rounded-xl border border-amber-400/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <h4 className="font-semibold text-amber-400">Contact Direct</h4>
                  </div>
                  <p className="text-sm text-slate-300">Pentru întrebări personale, sugestii sau colaborări</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-1 lg:order-2"
            >
              <img 
                src="https://images.unsplash.com/photo-1511632765486-a01980e01a18"
                alt="Comunitate Creștină în Rugăciune"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent rounded-3xl"></div>
              <div className="absolute top-4 right-4">
                <Heart className="w-8 h-8 text-amber-400" />
              </div>
              <div className="absolute bottom-4 left-4">
                <Cross className="w-6 h-6 text-amber-400" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-slate-800/50 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-amber-400 mb-4">Întrebări Frecvente</h2>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto px-4">
              Răspunsuri la întrebările cele mai comune
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "Cum pot participa la studiile biblice?",
                answer: "Poți să te alături grupurilor noastre WhatsApp sau să urmărești episoadele pe YouTube. Toate studiile sunt gratuite și deschise tuturor."
              },
              {
                question: "Cât de des sunt publicate episoade noi?",
                answer: "Încercăm să publicăm conținut nou în mod regulat. Abonează-te pe YouTube pentru a fi notificat despre episoadele noi."
              },
              {
                question: "Pot trimite cereri de rugăciune?",
                answer: "Da, absolut! Utilizează formularul de pe pagina de rugăciuni sau contactează-ne direct prin email."
              },
              {
                question: "Este comunitatea deschisă tuturor?",
                answer: "Da, comunitatea noastră este deschisă tuturor celor care doresc să crească spiritual și să înțeleagă mai bine Cuvântul lui Dumnezeu."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-700/50 p-6 rounded-2xl border border-amber-400/20"
              >
                <h3 className="text-lg font-semibold text-amber-400 mb-3">{faq.question}</h3>
                <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ContactPage;